import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import axios from 'axios';
import { toast } from 'sonner';
import { FaFolder, FaVideo, FaPlus, FaEdit, FaTrash, FaUsers, FaCheck, FaTimes } from 'react-icons/fa';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RecipeVideoManager = () => {
  const [activeTab, setActiveTab] = useState('folders');
  const [folders, setFolders] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Form states
  const [showFolderForm, setShowFolderForm] = useState(false);
  const [showVideoForm, setShowVideoForm] = useState(false);
  const [showAccessForm, setShowAccessForm] = useState(false);
  const [editingFolder, setEditingFolder] = useState(null);
  const [editingVideo, setEditingVideo] = useState(null);
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [folderAccess, setFolderAccess] = useState([]);
  
  const [folderData, setFolderData] = useState({
    name: '',
    description: '',
    thumbnail: '',
    price: 0,
    is_free: false,
    free_for_plans: [],
    order: 0
  });
  
  const [videoData, setVideoData] = useState({
    folder_id: '',
    title: '',
    description: '',
    video_url: '',
    thumbnail: '',
    duration: '',
    order: 0
  });
  
  const [accessData, setAccessData] = useState({
    user_email: '',
    folder_id: '',
    access_type: 'gifted'
  });

  const [subscriptionPrice, setSubscriptionPrice] = useState({ price_cop: 50000, price_usd: 12 });

  useEffect(() => {
    fetchFolders();
    fetchVideos();
    fetchSubscriptionPrice();
  }, []);

  const fetchFolders = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/recipe-videos/admin/all-folders`);
      setFolders(response.data || []);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar carpetas');
    } finally {
      setLoading(false);
    }
  };

  const fetchVideos = async (folderId = null) => {
    try {
      const url = folderId 
        ? `${API}/recipe-videos/admin/all-videos?folder_id=${folderId}`
        : `${API}/recipe-videos/admin/all-videos`;
      const response = await axios.get(url);
      setVideos(response.data || []);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchSubscriptionPrice = async () => {
    try {
      const response = await axios.get(`${API}/recipe-videos/subscription-price`);
      setSubscriptionPrice(response.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchFolderAccess = async (folderId) => {
    try {
      const response = await axios.get(`${API}/recipe-videos/admin/folder-access/${folderId}`);
      setFolderAccess(response.data || []);
    } catch (error) {
      console.error('Error:', error);
      setFolderAccess([]);
    }
  };

  // Folder CRUD
  const handleSaveFolder = async () => {
    try {
      if (editingFolder) {
        await axios.put(`${API}/recipe-videos/admin/folders/${editingFolder.id}`, folderData);
        toast.success('Carpeta actualizada');
      } else {
        await axios.post(`${API}/recipe-videos/admin/folders`, folderData);
        toast.success('Carpeta creada');
      }
      fetchFolders();
      resetFolderForm();
    } catch (error) {
      toast.error('Error al guardar carpeta');
    }
  };

  const handleEditFolder = (folder) => {
    setEditingFolder(folder);
    setFolderData({
      name: folder.name || '',
      description: folder.description || '',
      thumbnail: folder.thumbnail || '',
      price: folder.price || 0,
      is_free: folder.is_free || false,
      free_for_plans: folder.free_for_plans || [],
      order: folder.order || 0
    });
    setShowFolderForm(true);
  };

  const handleDeleteFolder = async (folderId) => {
    if (!confirm('¿Eliminar esta carpeta y todos sus videos?')) return;
    try {
      await axios.delete(`${API}/recipe-videos/admin/folders/${folderId}`);
      toast.success('Carpeta eliminada');
      fetchFolders();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const resetFolderForm = () => {
    setShowFolderForm(false);
    setEditingFolder(null);
    setFolderData({
      name: '',
      description: '',
      thumbnail: '',
      price: 0,
      is_free: false,
      free_for_plans: [],
      order: 0
    });
  };

  // Video CRUD
  const handleSaveVideo = async () => {
    try {
      if (editingVideo) {
        await axios.put(`${API}/recipe-videos/admin/videos/${editingVideo.id}`, videoData);
        toast.success('Video actualizado');
      } else {
        await axios.post(`${API}/recipe-videos/admin/videos`, videoData);
        toast.success('Video creado');
      }
      fetchVideos();
      resetVideoForm();
    } catch (error) {
      toast.error('Error al guardar video');
    }
  };

  const handleEditVideo = (video) => {
    setEditingVideo(video);
    setVideoData({
      folder_id: video.folder_id || '',
      title: video.title || '',
      description: video.description || '',
      video_url: video.video_url || '',
      thumbnail: video.thumbnail || '',
      duration: video.duration || '',
      order: video.order || 0
    });
    setShowVideoForm(true);
  };

  const handleDeleteVideo = async (videoId) => {
    if (!confirm('¿Eliminar este video?')) return;
    try {
      await axios.delete(`${API}/recipe-videos/admin/videos/${videoId}`);
      toast.success('Video eliminado');
      fetchVideos();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const resetVideoForm = () => {
    setShowVideoForm(false);
    setEditingVideo(null);
    setVideoData({
      folder_id: '',
      title: '',
      description: '',
      video_url: '',
      thumbnail: '',
      duration: '',
      order: 0
    });
  };

  // Access management
  const handleGrantAccess = async () => {
    try {
      await axios.post(`${API}/recipe-videos/admin/grant-access`, accessData);
      toast.success('Acceso otorgado');
      if (selectedFolderId) fetchFolderAccess(selectedFolderId);
      setAccessData({ user_email: '', folder_id: selectedFolderId || '', access_type: 'gifted' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al otorgar acceso');
    }
  };

  const handleRevokeAccess = async (accessId) => {
    if (!confirm('¿Revocar este acceso?')) return;
    try {
      await axios.delete(`${API}/recipe-videos/admin/revoke-access/${accessId}`);
      toast.success('Acceso revocado');
      if (selectedFolderId) fetchFolderAccess(selectedFolderId);
    } catch (error) {
      toast.error('Error al revocar acceso');
    }
  };

  const handleUpdateSubscriptionPrice = async () => {
    try {
      await axios.put(`${API}/recipe-videos/admin/subscription-price`, subscriptionPrice);
      toast.success('Precio actualizado');
    } catch (error) {
      toast.error('Error al actualizar precio');
    }
  };

  const plans = [
    { id: 'basic', label: 'Fit Evolution' },
    { id: 'premium', label: 'Premium' },
    { id: 'gold', label: 'Gold' },
    { id: 'vip', label: 'VIP' },
    { id: 'competitivo', label: 'Competitivo' }
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-white flex items-center gap-2">
          <FaVideo className="text-[var(--brand-primary)]" />
          Recetarios en Video
        </h2>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-700 pb-2">
        <Button
          variant={activeTab === 'folders' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('folders')}
          className={activeTab === 'folders' ? 'bg-[var(--brand-primary)]' : ''}
        >
          <FaFolder className="mr-2" /> Carpetas ({folders.length})
        </Button>
        <Button
          variant={activeTab === 'videos' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('videos')}
          className={activeTab === 'videos' ? 'bg-[var(--brand-primary)]' : ''}
        >
          <FaVideo className="mr-2" /> Videos ({videos.length})
        </Button>
        <Button
          variant={activeTab === 'pricing' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('pricing')}
          className={activeTab === 'pricing' ? 'bg-[var(--brand-primary)]' : ''}
        >
          💰 Precios
        </Button>
      </div>

      {/* CARPETAS TAB */}
      {activeTab === 'folders' && (
        <div className="space-y-4">
          <Button onClick={() => setShowFolderForm(true)} className="bg-green-600 hover:bg-green-700">
            <FaPlus className="mr-2" /> Nueva Carpeta
          </Button>

          {/* Form de carpeta */}
          {showFolderForm && (
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white">
                  {editingFolder ? 'Editar Carpeta' : 'Nueva Carpeta'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-300">Nombre *</Label>
                    <Input
                      value={folderData.name}
                      onChange={(e) => setFolderData({...folderData, name: e.target.value})}
                      className="bg-gray-700 border-gray-600 text-white"
                      placeholder="Ej: Desayunos Saludables"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">Precio (COP)</Label>
                    <Input
                      type="number"
                      value={folderData.price}
                      onChange={(e) => setFolderData({...folderData, price: parseFloat(e.target.value) || 0})}
                      className="bg-gray-700 border-gray-600 text-white"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-gray-300">Descripción</Label>
                  <Textarea
                    value={folderData.description}
                    onChange={(e) => setFolderData({...folderData, description: e.target.value})}
                    className="bg-gray-700 border-gray-600 text-white"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">URL de Imagen (thumbnail)</Label>
                  <Input
                    value={folderData.thumbnail}
                    onChange={(e) => setFolderData({...folderData, thumbnail: e.target.value})}
                    className="bg-gray-700 border-gray-600 text-white"
                    placeholder="https://..."
                  />
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={folderData.is_free}
                      onCheckedChange={(checked) => setFolderData({...folderData, is_free: checked})}
                    />
                    <Label className="text-gray-300">Gratis para todos</Label>
                  </div>
                  <div>
                    <Label className="text-gray-300">Orden</Label>
                    <Input
                      type="number"
                      value={folderData.order}
                      onChange={(e) => setFolderData({...folderData, order: parseInt(e.target.value) || 0})}
                      className="bg-gray-700 border-gray-600 text-white w-20"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-gray-300">Gratis para planes:</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {plans.map(plan => (
                      <Badge
                        key={plan.id}
                        className={`cursor-pointer ${folderData.free_for_plans.includes(plan.id) ? 'bg-green-600' : 'bg-gray-600'}`}
                        onClick={() => {
                          const newPlans = folderData.free_for_plans.includes(plan.id)
                            ? folderData.free_for_plans.filter(p => p !== plan.id)
                            : [...folderData.free_for_plans, plan.id];
                          setFolderData({...folderData, free_for_plans: newPlans});
                        }}
                      >
                        {plan.label}
                      </Badge>
                    ))}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleSaveFolder} className="bg-[var(--brand-primary)]">Guardar</Button>
                  <Button variant="outline" onClick={resetFolderForm}>Cancelar</Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Lista de carpetas */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {folders.map(folder => (
              <Card key={folder.id} className={`bg-gray-800 border-gray-700 ${!folder.is_active ? 'opacity-50' : ''}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    {folder.thumbnail && (
                      <img src={folder.thumbnail} alt={folder.name} className="w-16 h-16 rounded object-cover" />
                    )}
                    <div className="flex-1">
                      <h3 className="font-semibold text-white">{folder.name}</h3>
                      <p className="text-sm text-gray-400">{folder.video_count || 0} videos</p>
                      <p className="text-sm text-[#00F0FF]">
                        {folder.is_free ? 'GRATIS' : `$${folder.price?.toLocaleString('es-CO')} COP`}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Button size="sm" variant="outline" onClick={() => handleEditFolder(folder)}>
                      <FaEdit className="mr-1" /> Editar
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => {
                        setSelectedFolderId(folder.id);
                        fetchFolderAccess(folder.id);
                        setShowAccessForm(true);
                        setAccessData({...accessData, folder_id: folder.id});
                      }}
                    >
                      <FaUsers className="mr-1" /> Accesos
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => handleDeleteFolder(folder.id)}>
                      <FaTrash />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* VIDEOS TAB */}
      {activeTab === 'videos' && (
        <div className="space-y-4">
          <div className="flex gap-2">
            <Button onClick={() => setShowVideoForm(true)} className="bg-green-600 hover:bg-green-700">
              <FaPlus className="mr-2" /> Nuevo Video
            </Button>
            <select
              className="bg-gray-700 border border-gray-600 text-white rounded px-3"
              onChange={(e) => fetchVideos(e.target.value || null)}
            >
              <option value="">Todas las carpetas</option>
              {folders.map(f => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
          </div>

          {/* Form de video */}
          {showVideoForm && (
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white">
                  {editingVideo ? 'Editar Video' : 'Nuevo Video'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-300">Carpeta *</Label>
                    <select
                      value={videoData.folder_id}
                      onChange={(e) => setVideoData({...videoData, folder_id: e.target.value})}
                      className="w-full bg-gray-700 border border-gray-600 text-white rounded px-3 py-2"
                    >
                      <option value="">Seleccionar carpeta</option>
                      {folders.filter(f => f.is_active).map(f => (
                        <option key={f.id} value={f.id}>{f.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-gray-300">Título *</Label>
                    <Input
                      value={videoData.title}
                      onChange={(e) => setVideoData({...videoData, title: e.target.value})}
                      className="bg-gray-700 border-gray-600 text-white"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-gray-300">URL del Video (YouTube, Vimeo, o directo) *</Label>
                  <Input
                    value={videoData.video_url}
                    onChange={(e) => setVideoData({...videoData, video_url: e.target.value})}
                    className="bg-gray-700 border-gray-600 text-white"
                    placeholder="https://youtube.com/watch?v=... o https://vimeo.com/..."
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-300">Thumbnail URL</Label>
                    <Input
                      value={videoData.thumbnail}
                      onChange={(e) => setVideoData({...videoData, thumbnail: e.target.value})}
                      className="bg-gray-700 border-gray-600 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">Duración</Label>
                    <Input
                      value={videoData.duration}
                      onChange={(e) => setVideoData({...videoData, duration: e.target.value})}
                      className="bg-gray-700 border-gray-600 text-white"
                      placeholder="5:30"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-gray-300">Descripción</Label>
                  <Textarea
                    value={videoData.description}
                    onChange={(e) => setVideoData({...videoData, description: e.target.value})}
                    className="bg-gray-700 border-gray-600 text-white"
                  />
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleSaveVideo} className="bg-[var(--brand-primary)]">Guardar</Button>
                  <Button variant="outline" onClick={resetVideoForm}>Cancelar</Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Lista de videos */}
          <div className="space-y-2">
            {videos.map(video => {
              const folder = folders.find(f => f.id === video.folder_id);
              return (
                <Card key={video.id} className={`bg-gray-800 border-gray-700 ${!video.is_active ? 'opacity-50' : ''}`}>
                  <CardContent className="p-3 flex items-center gap-4">
                    {video.thumbnail && (
                      <img src={video.thumbnail} alt={video.title} className="w-24 h-16 rounded object-cover" />
                    )}
                    <div className="flex-1">
                      <h4 className="font-medium text-white">{video.title}</h4>
                      <p className="text-sm text-gray-400">
                        Carpeta: {folder?.name || 'Sin carpeta'} | {video.duration || 'Sin duración'}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => handleEditVideo(video)}>
                        <FaEdit />
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => handleDeleteVideo(video.id)}>
                        <FaTrash />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* PRECIOS TAB */}
      {activeTab === 'pricing' && (
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">Precio de Suscripción Mensual</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-gray-400">
              Este es el precio que los usuarios pagarán para tener acceso a TODOS los videos de recetas por un mes.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300">Precio en COP</Label>
                <Input
                  type="number"
                  value={subscriptionPrice.price_cop}
                  onChange={(e) => setSubscriptionPrice({...subscriptionPrice, price_cop: parseFloat(e.target.value) || 0})}
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>
              <div>
                <Label className="text-gray-300">Precio en USD</Label>
                <Input
                  type="number"
                  value={subscriptionPrice.price_usd}
                  onChange={(e) => setSubscriptionPrice({...subscriptionPrice, price_usd: parseFloat(e.target.value) || 0})}
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>
            </div>
            <Button onClick={handleUpdateSubscriptionPrice} className="bg-[var(--brand-primary)]">
              Guardar Precios
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Modal de Accesos */}
      {showAccessForm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={() => setShowAccessForm(false)}>
          <Card className="bg-gray-800 border-gray-700 w-full max-w-lg" onClick={e => e.stopPropagation()}>
            <CardHeader>
              <CardTitle className="text-white flex items-center justify-between">
                <span>Gestionar Accesos</span>
                <Button size="sm" variant="ghost" onClick={() => setShowAccessForm(false)}>
                  <FaTimes />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-gray-300">Email del usuario</Label>
                <Input
                  value={accessData.user_email}
                  onChange={(e) => setAccessData({...accessData, user_email: e.target.value})}
                  className="bg-gray-700 border-gray-600 text-white"
                  placeholder="usuario@email.com"
                />
              </div>
              <Button onClick={handleGrantAccess} className="bg-green-600">
                <FaCheck className="mr-2" /> Otorgar Acceso
              </Button>

              <div className="border-t border-gray-700 pt-4">
                <h4 className="text-white font-medium mb-2">Usuarios con acceso:</h4>
                {folderAccess.length === 0 ? (
                  <p className="text-gray-500 text-sm">Ningún usuario tiene acceso individual</p>
                ) : (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {folderAccess.map(access => (
                      <div key={access.id} className="flex items-center justify-between bg-gray-700 rounded p-2">
                        <div>
                          <p className="text-white text-sm">{access.user_email}</p>
                          <p className="text-gray-400 text-xs">{access.access_type}</p>
                        </div>
                        <Button size="sm" variant="destructive" onClick={() => handleRevokeAccess(access.id)}>
                          <FaTimes />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default RecipeVideoManager;
